export type Job = {
  id: string;
  title: string;
  seniority: string;
  contract: string;
  company: string;
  anonymous: boolean;
  location: string;
  setting: string;
  payStructure: string;
  pay: string;
  advert: string;
  posted: string;
  logoTone: string;
  /** Tag ids from src/components/site/tags.ts */
  tags?: string[];
  /** ISO datetime, set on jobs loaded from the API — used to sort by recency. */
  createdAt?: string;
  /** Short reference, e.g. AS-1846. Doubles as the lowercase /jobs/ link code. */
  ref?: string;
  /** The role's old CL-xxxx reference, so /jobs/cl-xxxx links sent before the rename still open it. */
  legacyRef?: string;
  featured?: boolean;
};

/**
 * Live adverts. Roles appear here once an employer posts them through
 * /post-job. One example role is included so the flow can be tested.
 */
export const jobs: Job[] = [
  {
    id: "sales-executive-northline",
    title: "Sales Executive",
    seniority: "Mid-level",
    contract: "Full-time",
    company: "Northline Group",
    anonymous: false,
    location: "London, UK",
    setting: "Hybrid",
    payStructure: "Base salary + commission",
    pay: "£35,000 – £45,000 base + uncapped commission (OTE £80,000)",
    advert:
      "We sell workflow software to mid-market logistics companies and need a Sales Executive who can run their own pipeline end to end.\n\nYou will work inbound demo requests plus your own outbound, and you own the deal from first call to signature. Average deal size is £18k a year, cycles run three to six weeks.\n\nWe care about how you sell, not how your CV is formatted. Send us a 60-second pitch telling us why we should put you in front of our buyers.",
    posted: "Posted 2 days ago",
    logoTone: "bg-purple-soft",
    tags: ["urgent", "b2b", "saas", "base-plus-commission", "uncapped-commission", "hybrid", "mid-level", "warm-leads"],
  },
];

export const companyName = (j: Job) => (j.anonymous ? "Anonymous" : j.company);

/**
 * Cleans up freeform advert copy for display. Bulk-imported adverts often
 * arrive as one unbroken blob with a systematic "a/an" slip before the
 * contract type ("on a Internship basis") — this fixes that known pattern and,
 * when the text has no paragraph breaks of its own, splits it into readable
 * paragraphs so it isn't a single wall of text.
 */
export function formatAdvert(text: string): string {
  if (!text) return text;
  let out = text.replace(/\bon an?\s+(internship)\b/gi, "on an $1");
  if (!out.includes("\n")) {
    const sentences = out.match(/[^.!?]+[.!?]+(?:\s+|$)/g) ?? [out];
    const paragraphs: string[] = [];
    for (let i = 0; i < sentences.length; i += 2) {
      paragraphs.push(sentences.slice(i, i + 2).join("").trim());
    }
    out = paragraphs.join("\n\n");
  }
  return out;
}

/**
 * Section headings the admin job form writes into an advert ("Who they
 * are\n…", "Objectives\n• …"), mapped to the label the job page shows.
 */
const ADVERT_HEADINGS: Record<string, string> = {
  "who they are": "Who they are",
  "the role": "The role",
  objectives: "Your objectives",
  "your objectives": "Your objectives",
  "who they are looking for": "Who they are looking for",
  "what they get": "What they get",
};

export type AdvertSection = { label: string; text: string; items: string[] };

/**
 * Splits an advert written with the job form's headings into labelled
 * sections, with "•"/"-" lines as list items. Returns null for freeform
 * adverts with no known heading, so callers can fall back to paragraphs.
 * The "Type of role: …" / "Sector: …" lead block is dropped — both are
 * already shown elsewhere on the page.
 */
export function advertSections(text: string): AdvertSection[] | null {
  const blocks = (text || "").split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);
  const sections: AdvertSection[] = [];
  for (const block of blocks) {
    const [first = "", ...rest] = block.split("\n");
    const label = ADVERT_HEADINGS[first.trim().replace(/:$/, "").toLowerCase()];
    if (!label) {
      if (block.split("\n").every((l) => /^(type of role|sector):/i.test(l.trim()))) continue;
      // Text before the first heading, or between headings, stays with the previous section.
      const prev = sections[sections.length - 1];
      if (prev) prev.text = prev.text ? `${prev.text}\n\n${block}` : block;
      else sections.push({ label: "", text: block, items: [] });
      continue;
    }
    const lines = rest.map((l) => l.trim()).filter(Boolean);
    const bullet = /^[•\-*]\s*/;
    sections.push({
      label,
      text: lines.filter((l) => !bullet.test(l)).join("\n"),
      items: lines.filter((l) => bullet.test(l)).map((l) => l.replace(bullet, "")),
    });
  }
  return sections.some((s) => s.label) ? sections : null;
}

/** Turns an ISO datetime into the "Posted N days/weeks/months ago" copy used on job cards. */
export function postedLabel(createdAt: string): string {
  const days = Math.max(0, Math.floor((Date.now() - new Date(createdAt).getTime()) / 86_400_000));
  if (days <= 0) return "Posted today";
  if (days === 1) return "Posted 1 day ago";
  if (days < 14) return `Posted ${days} days ago`;
  if (days < 60) return `Posted ${Math.floor(days / 7)} weeks ago`;
  return `Posted ${Math.floor(days / 30)} months ago`;
}

const API_URL = (import.meta.env["VITE_API_URL"] as string | undefined)?.replace(/\/$/, "") ?? "";

type ApiJob = {
  id: string;
  title: string;
  seniority: string | null;
  contract: string | null;
  company: string | null;
  anonymous: boolean;
  location: string | null;
  setting: string | null;
  payStructure: string | null;
  pay: string | null;
  advert: string | null;
  tags: string[];
  createdAt: string | null;
  ref?: string | null;
  legacyRef?: string | null;
  featured?: boolean;
};

function apiJobToJob(j: ApiJob): Job {
  return {
    id: j.id,
    title: j.title,
    seniority: j.seniority ?? "",
    contract: j.contract ?? "",
    company: j.company ?? "",
    anonymous: j.anonymous,
    location: j.location ?? "",
    setting: j.setting ?? "",
    payStructure: j.payStructure ?? "",
    pay: j.pay ?? "",
    advert: j.advert ?? "",
    posted: j.createdAt ? postedLabel(j.createdAt) : "Posted recently",
    ...(j.createdAt ? { createdAt: j.createdAt } : {}),
    ...(j.ref ? { ref: j.ref } : {}),
    ...(j.legacyRef ? { legacyRef: j.legacyRef } : {}),
    ...(j.featured ? { featured: true } : {}),
    logoTone: "bg-purple-soft",
    tags: j.tags ?? [],
  };
}

/**
 * Every live, admin-approved job board advert — from MySQL via GET
 * /api/public/jobs. This is the real data source for /find-jobs and every
 * job detail/apply page.
 */
export async function fetchPublicJobs(): Promise<Job[]> {
  const res = await fetch(`${API_URL}/api/public/jobs`);
  if (!res.ok) throw new Error(`Could not load jobs (${res.status}).`);
  const body = (await res.json()) as { jobs: ApiJob[] };
  return body.jobs.map(apiJobToJob);
}

/**
 * The role a specific invitation points to — regardless of whether it's
 * live on the public board yet. fetchPublicJobs() only ever returns
 * status=Live AND decision=Approved jobs, which shut an invited candidate
 * out of viewing (and applying for) their own invited role until CLOSDD
 * happened to approve it separately; the invite's own unguessable token is
 * what authorizes seeing this one job, not its public-listing state.
 * Returns null for an invalid/expired/cancelled token or a deleted job.
 */
export async function fetchInviteJob(token: string): Promise<Job | null> {
  const key = token.trim();
  if (!key) return null;
  const res = await fetch(`${API_URL}/api/public/invites/${encodeURIComponent(key)}/job`);
  if (!res.ok) return null;
  const body = (await res.json()) as { job: ApiJob };
  return apiJobToJob(body.job);
}
